import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { CalendarIcon, Copy, Eye, List, Plus, Search, ShoppingCart, UserCheck, UserPlus, Users, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';

type ApplicationCustomerType = 'NEW_CUSTOMERS_ONLY' | 'RETURNING_CUSTOMERS_ONLY' | 'ALL_CUSTOMERS';

type DiscountCodeFormData = {
    name: string;
    description: string;
    type: 'ABSOLUTE_DISCOUNT' | 'PERCENTAGE_DISCOUNT';
    discountValue: string;
    applicationLevel: 'PER_LINE_ITEM' | 'ENTIRE_PURCHASE';
    applicationCustomerType: ApplicationCustomerType;
    startDate: Date | undefined;
    endDate: Date | undefined;
    validDaysOfWeek: number[];
    validTimeWindowStart: string;
    validTimeWindowEnd: string;
};

const emptyFormData: DiscountCodeFormData = {
    name: '',
    description: '',
    type: 'PERCENTAGE_DISCOUNT',
    discountValue: '',
    applicationLevel: 'PER_LINE_ITEM',
    applicationCustomerType: 'ALL_CUSTOMERS',
    startDate: undefined,
    endDate: undefined,
    validDaysOfWeek: [],
    validTimeWindowStart: '',
    validTimeWindowEnd: '',
};

const customerTypeLabelMap: Record<ApplicationCustomerType, string> = {
    ALL_CUSTOMERS: 'Alle Kunden',
    NEW_CUSTOMERS_ONLY: 'Nur Neukunden',
    RETURNING_CUSTOMERS_ONLY: 'Nur Bestandskunden',
};

type DatePickerProps = {
    date: Date | undefined;
    onSelect: (date: Date | undefined) => void;
    placeholder: string;
};

const DatePicker = ({ date, onSelect, placeholder }: DatePickerProps) => {
    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant='outline'
                    className={cn(
                        'w-full justify-start text-left font-normal',
                        !date && 'text-muted-foreground'
                    )}
                >
                    <CalendarIcon className='mr-2 h-4 w-4' />
                    {date ? format(date, 'dd.MM.yyyy', { locale: de }) : placeholder}
                </Button>
            </PopoverTrigger>
            <PopoverContent className='w-auto p-0' align='start'>
                <Calendar
                    mode='single'
                    selected={date}
                    onSelect={onSelect}
                />
            </PopoverContent>
        </Popover>
    );
};

const getStatusBadge = (startDate: Date, endDate: Date) => {
    const now = new Date();
    if (now < new Date(startDate)) {
        return <Badge variant='outline' className='text-blue-600 border-blue-300'>Geplant</Badge>;
    }
    if (now > new Date(endDate)) {
        return <Badge variant='outline' className='text-gray-500 border-gray-300'>Abgelaufen</Badge>;
    }
    return <Badge variant='outline' className='text-green-600 border-green-300'>Aktiv</Badge>;
};

const formatDiscountValue = (type: string, value: number | string) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (type === 'PERCENTAGE_DISCOUNT') {
        return `${num}%`;
    }
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(num);
};

type ApplicationLevelCardProps = {
    selected: boolean;
    onClick: () => void;
    title: string;
    description: string;
    example: string;
    icon: React.ReactNode;
};

const ApplicationLevelCard = ({ selected, onClick, title, description, example, icon }: ApplicationLevelCardProps) => {
    return (
        <button
            type='button'
            onClick={onClick}
            className={cn(
                'flex flex-col gap-2 p-4 rounded-lg border-2 text-left transition-all',
                selected
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
            )}
        >
            <div className='flex items-center gap-2'>
                <div className={cn(
                    'flex items-center justify-center w-8 h-8 rounded-full',
                    selected ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-500'
                )}>
                    {icon}
                </div>
                <span className={cn('font-medium text-sm', selected ? 'text-primary' : 'text-gray-700')}>
                    {title}
                </span>
            </div>
            <p className='text-xs text-muted-foreground leading-relaxed'>
                {description}
            </p>
            <p className='text-xs text-muted-foreground/70 italic'>
                {example}
            </p>
        </button>
    );
};

const DiscountCodesEditor = () => {
    const navigate = useNavigate();
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [formData, setFormData] = useState<DiscountCodeFormData>(emptyFormData);
    const [selectedStoreIds, setSelectedStoreIds] = useState<Set<number>>(new Set());
    const [searchQuery, setSearchQuery] = useState('');

    const utils = trpc.useUtils();

    const { data: codesData, isLoading } = trpc.discountCodes.getAll.useQuery();
    const { data: storesData } = trpc.discountCodes.getAllStores.useQuery();
    const stores = storesData?.data ?? [];

    const openCreateDialog = () => {
        setFormData(emptyFormData);
        setSelectedStoreIds(new Set(stores.map((s) => s.id)));
        setIsCreateDialogOpen(true);
    };

    const toggleStore = (storeId: number) => {
        setSelectedStoreIds((prev) => {
            const next = new Set(prev);
            if (next.has(storeId)) {
                next.delete(storeId);
            } else {
                next.add(storeId);
            }
            return next;
        });
    };

    const allStoresSelected = stores.length > 0 && stores.every((s) => selectedStoreIds.has(s.id));

    const duplicateMutation = trpc.discountCodes.duplicate.useMutation({
        onSuccess: (result) => {
            toast.success('Rabattcode dupliziert');
            utils.discountCodes.getAll.invalidate();
            navigate(`/cms/discount-codes/${result.data.id}`);
        },
        onError: (error) => {
            toast.error('Fehler', { description: error.message });
        },
    });

    const createMutation = trpc.discountCodes.create.useMutation({
        onSuccess: (result) => {
            toast.success('Rabattcode erstellt');
            utils.discountCodes.getAll.invalidate();
            setIsCreateDialogOpen(false);
            setFormData(emptyFormData);
            navigate(`/cms/discount-codes/${result.data.id}`);
        },
        onError: (error) => {
            toast.error('Fehler', { description: error.message });
        },
    });

    const validateAndSubmit = () => {
        if (!formData.name.trim()) {
            toast.error('Bitte geben Sie einen Namen ein');
            return;
        }
        if (!formData.discountValue || parseFloat(formData.discountValue) <= 0) {
            toast.error('Bitte geben Sie einen gültigen Rabattwert ein');
            return;
        }
        if (formData.type === 'PERCENTAGE_DISCOUNT' && parseFloat(formData.discountValue) > 100) {
            toast.error('Prozentwert darf nicht über 100 liegen');
            return;
        }
        if (!formData.startDate) {
            toast.error('Bitte wählen Sie ein Startdatum');
            return;
        }
        if (!formData.endDate) {
            toast.error('Bitte wählen Sie ein Enddatum');
            return;
        }
        if (formData.endDate <= formData.startDate) {
            toast.error('Enddatum muss nach dem Startdatum liegen');
            return;
        }

        createMutation.mutate({
            name: formData.name.trim(),
            description: formData.description.trim(),
            type: formData.type,
            discountValue: parseFloat(formData.discountValue),
            applicationLevel: formData.applicationLevel,
            applicationCustomerType: formData.applicationCustomerType,
            startDate: formData.startDate.toISOString(),
            endDate: formData.endDate.toISOString(),
            validDaysOfWeek: formData.validDaysOfWeek,
            validTimeWindowStart: formData.validTimeWindowStart || null,
            validTimeWindowEnd: formData.validTimeWindowEnd || null,
            storeIds: Array.from(selectedStoreIds),
        });
    };

    const codes = codesData?.data || [];

    const filteredCodes = useMemo(() => {
        if (!searchQuery.trim()) return codes;
        const query = searchQuery.toLowerCase();
        return codes.filter((code) => code.name.toLowerCase().includes(query));
    }, [codes, searchQuery]);

    if (isLoading) {
        return (
            <div className='space-y-4'>
                <Skeleton className='h-10 w-48' />
                <Skeleton className='h-64 w-full' />
            </div>
        );
    }

    return (
        <div className='space-y-6'>
            <div className='flex justify-between items-center'>
                <p className='text-sm text-muted-foreground'>
                    Erstellen und verwalten Sie Rabattcodes für buchbare Services und Bundles.
                </p>
                <Button onClick={openCreateDialog}>
                    <Plus className='mr-2 h-4 w-4' />
                    Neuer Rabattcode
                </Button>
            </div>

            {codes.length === 0 ? (
                <div className='bg-white rounded-lg shadow-sm p-6'>
                    <div className='py-12 text-center text-muted-foreground'>
                        Noch keine Rabattcodes erstellt. Klicken Sie auf &quot;Neuer Rabattcode&quot; um zu beginnen.
                    </div>
                </div>
            ) : (
                <div className='bg-white rounded-lg shadow-sm p-6 space-y-4'>
                    <div className='relative'>
                        <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                        <Input
                            placeholder='Rabattcode suchen...'
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className='pl-9'
                        />
                        {searchQuery && (
                            <Button
                                variant='ghost'
                                size='icon'
                                className='absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7'
                                onClick={() => setSearchQuery('')}
                            >
                                <X className='h-3 w-3' />
                            </Button>
                        )}
                    </div>
                    <div className='rounded-md border border-gray-200 overflow-hidden'>
                        <Table>
                            <TableHeader className='bg-gray-50'>
                                <TableRow>
                                    <TableHead className='font-medium text-gray-700'>Name</TableHead>
                                    <TableHead className='font-medium text-gray-700'>Typ</TableHead>
                                    <TableHead className='font-medium text-gray-700 text-right'>Wert</TableHead>
                                    <TableHead className='font-medium text-gray-700'>Anwendung</TableHead>
                                    <TableHead className='font-medium text-gray-700'>Kundentyp</TableHead>
                                    <TableHead className='font-medium text-gray-700'>Gültig von</TableHead>
                                    <TableHead className='font-medium text-gray-700'>Gültig bis</TableHead>
                                    <TableHead className='font-medium text-gray-700 text-center'>Status</TableHead>
                                    <TableHead className='font-medium text-gray-700 text-center'>Zuordnungen</TableHead>
                                    <TableHead className='font-medium text-gray-700 text-center'>Nutzungen</TableHead>
                                    <TableHead className='font-medium text-gray-700 text-right'>Aktionen</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredCodes.map((code) => (
                                    <TableRow
                                        key={code.id}
                                        className='hover:bg-gray-50 transition-colors cursor-pointer'
                                        onClick={() => navigate(`/cms/discount-codes/${code.id}`)}
                                    >
                                        <TableCell className='font-medium'>{code.name}</TableCell>
                                        <TableCell>
                                            <Badge variant='secondary' className='text-xs'>
                                                {code.type === 'PERCENTAGE_DISCOUNT' ? 'Prozent' : 'Absolut'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className='text-right'>
                                            {formatDiscountValue(code.type, code.discountValue)}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant='outline' className='text-xs'>
                                                {code.applicationLevel === 'ENTIRE_PURCHASE' ? 'Gesamtbetrag' : 'Pro Einzelposition'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant='outline' className='text-xs'>
                                                {customerTypeLabelMap[code.applicationCustomerType]}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            {format(new Date(code.startDate), 'dd.MM.yyyy', { locale: de })}
                                        </TableCell>
                                        <TableCell>
                                            {format(new Date(code.endDate), 'dd.MM.yyyy', { locale: de })}
                                        </TableCell>
                                        <TableCell className='text-center'>
                                            {getStatusBadge(new Date(code.startDate), new Date(code.endDate))}
                                        </TableCell>
                                        <TableCell className='text-center'>
                                            <span className='text-sm'>
                                                {code.applicationLevel === 'ENTIRE_PURCHASE'
                                                    ? 'Alle'
                                                    : (code._count.validBookableItems + code._count.validBundles)
                                                }
                                            </span>
                                        </TableCell>
                                        <TableCell className='text-center'>
                                            <span className='text-sm'>
                                                {code._count.billingDraftLineItemDiscounts}
                                            </span>
                                        </TableCell>
                                        <TableCell className='text-right'>
                                            <div className='flex items-center justify-end gap-1'>
                                                <Button
                                                    variant='ghost'
                                                    size='icon'
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        duplicateMutation.mutate({ id: code.id });
                                                    }}
                                                    disabled={duplicateMutation.isPending}
                                                    title='Rabattcode duplizieren'
                                                >
                                                    <Copy className='h-4 w-4' />
                                                </Button>
                                                <Button
                                                    variant='ghost'
                                                    size='icon'
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/cms/discount-codes/${code.id}`);
                                                    }}
                                                    title='Details anzeigen'
                                                >
                                                    <Eye className='h-4 w-4' />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            )}

            {/* Create Dialog */}
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogContent className='max-w-2xl max-h-[85vh] overflow-y-auto'>
                    <DialogHeader>
                        <DialogTitle>Neuen Rabattcode erstellen</DialogTitle>
                        <DialogDescription>
                            Erstellen Sie einen neuen Rabattcode. Die zugehörigen buchbaren Services und Bundles können nach der Erstellung konfiguriert werden.
                        </DialogDescription>
                    </DialogHeader>
                    <div className='space-y-4'>
                        <div className='space-y-2'>
                            <Label>Name</Label>
                            <Input
                                placeholder='z.B. SOMMER2026'
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>
                        <div className='space-y-2'>
                            <Label>Beschreibung</Label>
                            <Input
                                placeholder='z.B. Sommeraktion 2026 - 10% auf alle Hydrafacials'
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            />
                            <p className='text-xs text-muted-foreground'>
                                Optionale Beschreibung zur internen Dokumentation des Rabattcodes.
                            </p>
                        </div>
                        <div className='grid grid-cols-2 gap-4'>
                            <div className='space-y-2'>
                                <Label>Rabatttyp</Label>
                                <Select
                                    value={formData.type}
                                    onValueChange={(value: 'ABSOLUTE_DISCOUNT' | 'PERCENTAGE_DISCOUNT') =>
                                        setFormData({ ...formData, type: value })
                                    }
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value='PERCENTAGE_DISCOUNT'>Prozentualer Rabatt</SelectItem>
                                        <SelectItem value='ABSOLUTE_DISCOUNT'>Absoluter Rabatt (EUR)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className='space-y-2'>
                                <Label>
                                    {formData.type === 'PERCENTAGE_DISCOUNT' ? 'Prozent (%)' : 'Betrag (EUR)'}
                                </Label>
                                <Input
                                    type='number'
                                    step={formData.type === 'PERCENTAGE_DISCOUNT' ? '1' : '0.01'}
                                    min='0'
                                    max={formData.type === 'PERCENTAGE_DISCOUNT' ? '100' : undefined}
                                    placeholder={formData.type === 'PERCENTAGE_DISCOUNT' ? '10' : '25.00'}
                                    value={formData.discountValue}
                                    onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
                                />
                            </div>
                        </div>

                        {/* Application Level Select-Cards */}
                        <div className='space-y-2'>
                            <Label>Anwendungsebene</Label>
                            <div className='grid grid-cols-2 gap-3'>
                                <ApplicationLevelCard
                                    selected={formData.applicationLevel === 'PER_LINE_ITEM'}
                                    onClick={() => setFormData({ ...formData, applicationLevel: 'PER_LINE_ITEM' })}
                                    title='Pro Einzelposition'
                                    description='Der Rabatt wird auf die zutreffenden Buchungspositionen angewendet und anteilig verteilt. Sie können festlegen, für welche buchbaren Services und Bundles der Rabattcode gilt.'
                                    example='Beispiel: Absoluter Rabatt 20 EUR, 3 passende Behandlungen à 100 EUR → 20 EUR anteilig verteilt (je ~6,67 EUR) = 280 EUR Gesamtbetrag'
                                    icon={<List className='w-4 h-4' />}
                                />
                                <ApplicationLevelCard
                                    selected={formData.applicationLevel === 'ENTIRE_PURCHASE'}
                                    onClick={() => setFormData({ ...formData, applicationLevel: 'ENTIRE_PURCHASE' })}
                                    title='Auf Gesamtbetrag'
                                    description='Der Rabatt wird einmalig auf den Gesamtbetrag der Buchung angewendet und anteilig auf alle Positionen verteilt. Gilt automatisch für alle buchbaren Services und Bundles.'
                                    example='Beispiel: Absoluter Rabatt 20 EUR, 3 Behandlungen à 100 EUR = 300 EUR → 20 EUR Rabatt = 280 EUR Gesamtbetrag'
                                    icon={<ShoppingCart className='w-4 h-4' />}
                                />
                            </div>
                        </div>

                        {/* Customer Type Select-Cards */}
                        <div className='space-y-2'>
                            <Label>Kundentyp</Label>
                            <div className='grid grid-cols-3 gap-3'>
                                <ApplicationLevelCard
                                    selected={formData.applicationCustomerType === 'ALL_CUSTOMERS'}
                                    onClick={() => setFormData({ ...formData, applicationCustomerType: 'ALL_CUSTOMERS' })}
                                    title='Alle Kunden'
                                    description='Der Rabattcode gilt für alle Kunden, unabhängig davon ob sie neu oder bestehend sind.'
                                    example=''
                                    icon={<Users className='w-4 h-4' />}
                                />
                                <ApplicationLevelCard
                                    selected={formData.applicationCustomerType === 'NEW_CUSTOMERS_ONLY'}
                                    onClick={() => setFormData({ ...formData, applicationCustomerType: 'NEW_CUSTOMERS_ONLY' })}
                                    title='Nur Neukunden'
                                    description='Der Rabattcode kann nur von Neukunden verwendet werden, die noch keinen abgeschlossenen oder geplanten Termin hatten.'
                                    example=''
                                    icon={<UserPlus className='w-4 h-4' />}
                                />
                                <ApplicationLevelCard
                                    selected={formData.applicationCustomerType === 'RETURNING_CUSTOMERS_ONLY'}
                                    onClick={() => setFormData({ ...formData, applicationCustomerType: 'RETURNING_CUSTOMERS_ONLY' })}
                                    title='Nur Bestandskunden'
                                    description='Der Rabattcode kann nur von Bestandskunden verwendet werden, die mindestens einen abgeschlossenen oder geplanten Termin hatten.'
                                    example=''
                                    icon={<UserCheck className='w-4 h-4' />}
                                />
                            </div>
                        </div>

                        <div className='grid grid-cols-2 gap-4'>
                            <div className='space-y-2'>
                                <Label>Startdatum</Label>
                                <DatePicker
                                    date={formData.startDate}
                                    onSelect={(date) => setFormData({ ...formData, startDate: date })}
                                    placeholder='Startdatum wählen'
                                />
                            </div>
                            <div className='space-y-2'>
                                <Label>Enddatum</Label>
                                <DatePicker
                                    date={formData.endDate}
                                    onSelect={(date) => setFormData({ ...formData, endDate: date })}
                                    placeholder='Enddatum wählen'
                                />
                            </div>
                        </div>

                        {/* Days of Week */}
                        <div className='space-y-2'>
                            <Label>Gültige Wochentage</Label>
                            <div className='flex flex-wrap gap-2'>
                                {([
                                    { value: 1, label: 'Mo' },
                                    { value: 2, label: 'Di' },
                                    { value: 3, label: 'Mi' },
                                    { value: 4, label: 'Do' },
                                    { value: 5, label: 'Fr' },
                                    { value: 6, label: 'Sa' },
                                    { value: 7, label: 'So' },
                                ] as const).map(({ value, label }) => (
                                    <button
                                        key={value}
                                        type='button'
                                        onClick={() => {
                                            const days = formData.validDaysOfWeek.includes(value)
                                                ? formData.validDaysOfWeek.filter((d) => d !== value)
                                                : [...formData.validDaysOfWeek, value];
                                            setFormData({ ...formData, validDaysOfWeek: days });
                                        }}
                                        className={cn(
                                            'px-3 py-1.5 rounded-md text-sm font-medium border transition-all',
                                            formData.validDaysOfWeek.includes(value)
                                                ? 'bg-primary text-primary-foreground border-primary'
                                                : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                                        )}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                            <p className='text-xs text-muted-foreground'>
                                Keine Auswahl = an allen Tagen gültig
                            </p>
                        </div>

                        {/* Time Window */}
                        <div className='grid grid-cols-2 gap-4'>
                            <div className='space-y-2'>
                                <Label>Zeitfenster von</Label>
                                <Input
                                    type='time'
                                    value={formData.validTimeWindowStart}
                                    onChange={(e) => setFormData({ ...formData, validTimeWindowStart: e.target.value })}
                                />
                            </div>
                            <div className='space-y-2'>
                                <Label>Zeitfenster bis</Label>
                                <Input
                                    type='time'
                                    value={formData.validTimeWindowEnd}
                                    onChange={(e) => setFormData({ ...formData, validTimeWindowEnd: e.target.value })}
                                />
                            </div>
                            <p className='text-xs text-muted-foreground col-span-2'>
                                Beide leer = keine Zeiteinschränkung. Bezieht sich auf die Startzeit des Termins.
                            </p>
                        </div>

                        {/* Stores / Locations */}
                        <div className='space-y-2'>
                            <div className='flex items-center justify-between'>
                                <Label>Gültige Standorte</Label>
                                <Button
                                    type='button'
                                    variant='ghost'
                                    size='sm'
                                    className='h-7 text-xs'
                                    onClick={() => {
                                        if (allStoresSelected) {
                                            setSelectedStoreIds(new Set());
                                        } else {
                                            setSelectedStoreIds(new Set(stores.map((s) => s.id)));
                                        }
                                    }}
                                >
                                    {allStoresSelected ? 'Alle abwählen' : 'Alle auswählen'}
                                </Button>
                            </div>
                            <div className='grid grid-cols-2 gap-2 rounded-md border border-gray-200 p-3'>
                                {stores.map((store) => (
                                    <label
                                        key={store.id}
                                        className='flex items-center gap-2 cursor-pointer'
                                    >
                                        <Checkbox
                                            checked={selectedStoreIds.has(store.id)}
                                            onCheckedChange={() => toggleStore(store.id)}
                                        />
                                        <span className='text-sm'>{store.name}</span>
                                    </label>
                                ))}
                            </div>
                            <p className='text-xs text-muted-foreground'>
                                Alle ausgewählt = an allen Standorten gültig (auch für zukünftige Standorte).
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant='outline' onClick={() => {
                            setIsCreateDialogOpen(false);
                            setFormData(emptyFormData);
                        }}>
                            Abbrechen
                        </Button>
                        <Button onClick={validateAndSubmit} disabled={createMutation.isPending}>
                            {createMutation.isPending ? 'Erstelle...' : 'Erstellen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default DiscountCodesEditor;
